import { useTranslation } from "react-i18next";
import { Grid, Skeleton } from "@mui/material";
import MUIDataTable from "mui-datatables";
import NoMatchImage from "../../../../components/common/NoMatchImage/noMatchImage";
import { useEffect, useState } from "react";
import { getUser, getUserStatus } from "../../../../pages/userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../../../consts/codes";

import { FilterUsersClient } from "../../../../services/user/consultRolesService";
import { GetCredentialsNrps } from "../../../../services/user/infonavit/adminCredentials";
import { SelectedUsersPreview } from "./selectedUsersPreview";
import jwt_decode from "jwt-decode";

export const UserNRPList = ({ control, setValue, clientUser }) => {
  /**
   * Tomando el rol por el token
   */
  const jwtTokenInfo = localStorage.getItem("infoToken");

  let role = "";
  if (jwtTokenInfo) {
    const jwtDecodeTokenInfo = jwt_decode(jwtTokenInfo);

    role = jwtDecodeTokenInfo["custom:Role"];
  }

  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [data, setData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [noContent, setNoContent] = useState(false);

  const { t } = useTranslation();

  const columns = [
    {
      name: "nrp",
      label: t("App.infonavitUsers.tableForm.nrp"),
      options: {
        filter: false,
        sort: true,
        searchable: true,
      },
    },
    {
      name: "companyName",
      label: t("App.infonavitUsers.tableForm.company"),
      options: {
        filter: false,
        sort: false,
        searchable: true,
      },
    },
    {
      name: "rfc",
      label: t("App.infonavitUsers.tableForm.rfcPatron"),
      options: {
        filter: false,
        sort: false,
        searchable: true,
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "multiple",
    selectToolbarPlacement: "none",
    fixedHeader: false,
    responsive: "standard",
    searchPlaceholder: t("App.infonavitUsers.tableForm.filterPlaceholder"),
    tableBodyHeight: "20rem",
    rowsSelected: selectedData,
    onRowSelectionChange: (
      currentRowsSelected,
      allRowsSelected,
      rowsSelected
    ) => {
      setSelectedData(rowsSelected);
    },
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    pagination: true,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: <NoMatchImage />,
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  const skeletonOptions = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectableRows: "none",
    responsive: "standard",
    searchPlaceholder: t("App.filterPlaceholder.tableForm.filterPlaceHolder"),
    tableBodyHeight: "20rem",
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    pagination: false,
    textLabels: {
      body: {
        noMatch: <Skeleton />,
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
    },
  };

  /**
   * Use Effect
   */
  /**
   * Carga los usuarios
   */
  useEffect(() => {
    if (userStatus === "succeeded") fetchNrps();
  }, [userStatus]);

  /**
   * useEffect para actualizar la lista de NRPs seleccionados
   */
  useEffect(() => {
    const selectedNrps = data.filter((_, index) =>
      selectedData.includes(index)
    );
    const nrps = selectedNrps.map((row) => row.nrp);

    setValue("nrps", nrps);
  }, [selectedData, data]);

  /**
   * Función que consulta y settea la información de los NRPs
   */
  const fetchNrps = async () => {
    try {
      setIsLoadingData(true);

      const mainUser = userInfo.mainUser;
      const {
        status,
        data: { responseStatus, response },
      } = await GetCredentialsNrps(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        const tempData = response.map((row, index) => {
          return { ...row, index };
        });
        const preSelectedData = tempData.filter((v) =>
          clientUser?.nrps?.includes(v.nrp)
        );

        setData(tempData);
        setNoContent(false);
        setSelectedData(preSelectedData.map((row) => row.index));
      } else {
        setData([]);
        setNoContent(true);
      }

      setIsLoadingData(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Recibe un array con las filas seleccionadas y lo transforma a un array
   * con los índices de cada fila seleccionada
   * @returns
   */
  const handleSetSelectedData = (selectedDataArray) => {
    setSelectedData(selectedDataArray.map((row) => row.index));
  };

  return (
    // °° Grid principal °°
    <Grid container gap={2}>
      <Grid xs="12" sm item className="companies_info_container" padding="0">
        {!noContent ? (
          isLoadingData ? (
            <MUIDataTable
              className="dataTable__base datatable-style dataTable--modern"
              style={{ marginTop: "5%", display: "none", border: "none" }}
              data={data}
              columns={columns}
              options={skeletonOptions}
            />
          ) : (
            <MUIDataTable
              className="dataTable__base datatable-style dataTable--modern"
              style={{ marginTop: "5%", display: "none" }}
              data={data}
              columns={columns}
              options={options}
            />
          )
        ) : (
          <NoMatchImage />
        )}
      </Grid>

      <Grid
        xs="12"
        sm="auto"
        item
        sx={{
          padding: { sm: "0 1.5rem", md: "3rem 0 0 0" },
          marginTop: { xs: "0.5rem", md: "5rem" },
        }}
      >
        <p className="subHeading-primary" style={{ fontSize: "1.3rem" }}>
          {t("App.infonavitUsers.tableForm.selectedRowssTitle")}
        </p>

        <div
          className="scroll-table"
          style={{ maxHeight: "35rem", overflowY: "auto" }}
        >
          <SelectedUsersPreview
            data={data.filter((_, index) => selectedData.includes(index))}
            labelKey="nrp"
            setSelectedData={handleSetSelectedData}
          />
        </div>
      </Grid>
    </Grid>
  );
};
